import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Cookie, Shield, Settings, X } from 'lucide-react';
import { useCookieConsent, ConsentRegion } from '@/hooks/useCookieConsent';

const CookieConsent = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const isTurkish = currentLang === 'tr';
  const isFrench = currentLang === 'fr';
  const {
    region,
    isLoading,
    showBanner,
    acceptAll,
    rejectAll,
    acceptCustom,
  } = useCookieConsent();
  
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  if (isLoading || !showBanner) {
    return null;
  }

  const handleCustomSave = () => {
    acceptCustom({
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
    });
    setShowCustomize(false);
  };

  // Determine which banner to show based on region and language
  // Only show Turkey banner for Turkish users in Turkey region
  const effectiveRegion: ConsentRegion = region;

  // French translations
  const frenchTexts = {
    title: "Préférences de Cookies",
    description: "Nous utilisons des cookies pour améliorer votre navigation, analyser le trafic et personnaliser le contenu. Veuillez choisir vos préférences ci-dessous.",
    customize: "Personnaliser",
    rejectAll: "Tout Refuser",
    acceptAll: "Tout Accepter",
    cancel: "Annuler",
    save: "Enregistrer",
    essential: "Cookies Essentiels",
    essentialDesc: "Nécessaires au fonctionnement du site",
    analytics: "Cookies Analytiques",
    analyticsDesc: "Nous aident à comprendre comment les visiteurs utilisent notre site",
    marketing: "Cookies Marketing",
    marketingDesc: "Utilisés pour la publicité ciblée et le remarketing",
    privacyLink: "Politique de confidentialité",
    simpleText: "Nous utilisons des cookies pour améliorer votre expérience de navigation. Acceptez pour continuer.",
    learnMore: "En savoir plus sur notre politique de confidentialité"
  };

  // Get localized text based on language
  const getText = (en: string, tr: string, fr: string) => {
    if (isTurkish) return tr;
    if (isFrench) return fr;
    return en;
  };

  // KVKK Banner for Turkey region (shows in user's language)
  if (effectiveRegion === 'turkey') {
    const bannerText = getText(
      "This website uses cookies to enhance your experience. For detailed information, please review our",
      "Bu web sitesi deneyiminizi geliştirmek için çerezler kullanır. Detaylı bilgi için",
      "Ce site utilise des cookies pour améliorer votre expérience. Pour plus d'informations, consultez notre"
    );
    const privacyLinkText = getText("Privacy Notice", "KVKK Aydınlatma Metnimizi", "Avis de confidentialité");
    const cookieLinkText = getText("Cookie Policy", "Çerez Politikamızı", "Politique de cookies");
    const andText = getText("and", "ve", "et");
    const rejectText = getText("Reject", "Reddediyorum", "Refuser");
    const acceptText = getText("Accept", "Kabul Ediyorum", "Accepter");
    const privacyLink = `/${currentLang}/${isTurkish ? 'kvkk-aydinlatma-metni' : 'privacy-policy'}`;
    const cookieLink = `/${currentLang}/${isTurkish ? 'cerez-politikasi' : 'cookie-policy'}`;

    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-md border-t border-border shadow-lg animate-in slide-in-from-bottom-4 duration-300">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                <Cookie className="w-5 h-5 text-primary" />
              </div>
              <div className="space-y-1">
                <p className="text-sm text-foreground">
                  {bannerText}{' '}
                  <Link to={privacyLink} className="text-primary hover:underline font-medium">
                    {privacyLinkText}
                  </Link>{' '}
                  {andText}{' '}
                  <Link to={cookieLink} className="text-primary hover:underline font-medium">
                    {cookieLinkText}
                  </Link>.
                </p>
              </div>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={rejectAll}
                className="flex-1 md:flex-none"
              >
                {rejectText}
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="flex-1 md:flex-none"
              >
                {acceptText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // GDPR Banner for EU/UK (includes French and Turkish)
  if (effectiveRegion === 'eu') {
    const title = getText("Cookie Preferences", "Çerez Tercihleri", frenchTexts.title);
    const description = getText(
      "We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. Please choose your cookie preferences below.",
      "Tarama deneyiminizi geliştirmek, site trafiğini analiz etmek ve içeriği kişiselleştirmek için çerezler kullanıyoruz. Lütfen çerez tercihlerinizi aşağıdan seçin.",
      frenchTexts.description
    );
    const customizeText = getText("Customize", "Özelleştir", frenchTexts.customize);
    const rejectText = getText("Reject All", "Tümünü Reddet", frenchTexts.rejectAll);
    const acceptText = getText("Accept All", "Tümünü Kabul Et", frenchTexts.acceptAll);
    const privacyLink = `/${currentLang}/privacy-policy`;
    const privacyText = getText("Privacy Policy", "Gizlilik Politikası", frenchTexts.privacyLink);
    
    return (
      <>
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-md border-t border-border shadow-lg animate-in slide-in-from-bottom-4 duration-300">
          <div className="container mx-auto max-w-4xl">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-1 flex-1">
                  <p className="font-medium text-foreground">{title}</p>
                  <p className="text-sm text-muted-foreground">
                    {description}{' '}
                    <Link to={privacyLink} className="text-primary hover:underline">
                      {privacyText}
                    </Link>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCustomize(true)}
                  className="gap-2"
                >
                  <Settings className="w-4 h-4" />
                  {customizeText}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rejectAll}
                >
                  {rejectText}
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                >
                  {acceptText}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Customize Modal */}
        <Dialog open={showCustomize} onOpenChange={setShowCustomize}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                {getText("Cookie Settings", "Çerez Ayarları", "Paramètres des Cookies")}
              </DialogTitle>
              <DialogDescription>
                {getText(
                  "Manage your cookie preferences. Essential cookies are required for the website to function properly.",
                  "Çerez tercihlerinizi yönetin. Zorunlu çerezler web sitesinin düzgün çalışması için gereklidir.",
                  "Gérez vos préférences de cookies. Les cookies essentiels sont nécessaires au bon fonctionnement du site."
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Essential Cookies */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="space-y-0.5">
                  <p className="font-medium text-sm">{getText("Essential Cookies", "Zorunlu Çerezler", frenchTexts.essential)}</p>
                  <p className="text-xs text-muted-foreground">{getText("Required for the website to function", "Web sitesinin çalışması için gerekli", frenchTexts.essentialDesc)}</p>
                </div>
                <Switch checked={true} disabled />
              </div>
              
              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="space-y-0.5">
                  <p className="font-medium text-sm">{getText("Analytics Cookies", "Analitik Çerezler", frenchTexts.analytics)}</p>
                  <p className="text-xs text-muted-foreground">{getText("Help us understand how visitors use our site", "Ziyaretçilerin sitemizi nasıl kullandığını anlamamıza yardımcı olur", frenchTexts.analyticsDesc)}</p>
                </div>
                <Switch 
                  checked={analyticsEnabled} 
                  onCheckedChange={setAnalyticsEnabled}
                />
              </div>
              
              {/* Marketing Cookies */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="space-y-0.5">
                  <p className="font-medium text-sm">{getText("Marketing Cookies", "Pazarlama Çerezleri", frenchTexts.marketing)}</p>
                  <p className="text-xs text-muted-foreground">{getText("Used for targeted advertising and remarketing", "Hedefli reklamcılık ve yeniden pazarlama için kullanılır", frenchTexts.marketingDesc)}</p>
                </div>
                <Switch 
                  checked={marketingEnabled} 
                  onCheckedChange={setMarketingEnabled}
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCustomize(false)}>
                {getText("Cancel", "İptal", frenchTexts.cancel)}
              </Button>
              <Button onClick={handleCustomSave}>
                {getText("Save Preferences", "Tercihleri Kaydet", frenchTexts.save)}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Simple Banner for Other Regions
  const simpleText = getText(
    "We use cookies to improve your browsing experience. Accept to continue.",
    "Tarama deneyiminizi geliştirmek için çerezler kullanıyoruz. Devam etmek için kabul edin.",
    frenchTexts.simpleText
  );
  const learnMoreText = getText(
    "Learn more about our privacy policy",
    "Gizlilik politikamız hakkında daha fazla bilgi edinin",
    frenchTexts.learnMore
  );
  const privacyLink = `/${currentLang}/privacy-policy`;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-md border-t border-border shadow-lg animate-in slide-in-from-bottom-4 duration-300">
      <div className="container mx-auto max-w-4xl">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm text-foreground">
              {simpleText}{' '}
              <Link to={privacyLink} className="text-primary hover:underline">
                {learnMoreText}
              </Link>
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={rejectAll}
              className="flex-1 sm:flex-none"
            >
              {getText("Reject", "Reddet", "Refuser")}
            </Button>
            <Button
              size="sm"
              onClick={acceptAll}
              className="flex-1 sm:flex-none"
            >
              {getText("Accept", "Kabul Et", "Accepter")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
